import { Injectable } from '@angular/core';
import { CasinoGameListService } from '@app/services/casino-game-list.service';
import { BehaviorSubject, Observable, Subscription, interval, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

type PragmaticLiveStatus = 'live' | 'offline' | 'unknown';
type DecorateGamesOptions = {
    enableLiveStatus?: boolean;
};

@Injectable({ providedIn: 'root' })
export class PragmaticLiveStatusService {
    private readonly snapshotCacheMs = 30_000;
    private readonly pollingIntervalMs = 15_000;

    private snapshotCache: any[] | null = null;
    private snapshotCachedAt = 0;
    private snapshotRequest$: Observable<any[]> | null = null;

    private readonly realtimeUpdates$ = new BehaviorSubject<Map<string, any>>(new Map());
    private pollingSubscription: Subscription | null = null;

    constructor(private casinoGameListService: CasinoGameListService) {}

    decorateGames$(games: any[], options?: DecorateGamesOptions): Observable<any[]> {
        const pragmaticGames = games.filter((game) => this.isPragmaticGame(game));
        if (!options?.enableLiveStatus || !pragmaticGames.length) {
            return of(games);
        }

        return this.getLiveSnapshot$().pipe(
            switchMap((liveGames) => {
                this.ensureRealtimeFeed(pragmaticGames, liveGames);
                return this.realtimeUpdates$.pipe(
                    startWith(this.realtimeUpdates$.value),
                    map((realtimeMap) =>
                        this.decorateGames(games, this.mergeLiveGames(liveGames, realtimeMap)),
                    ),
                );
            }),
            catchError(() => of(games)),
        );
    }

    private getLiveSnapshot$(forceRefresh: boolean = false): Observable<any[]> {
        const now = Date.now();
        if (!forceRefresh && this.snapshotCache && now - this.snapshotCachedAt < this.snapshotCacheMs) {
            return of(this.snapshotCache);
        }

        if (!forceRefresh && this.snapshotRequest$) {
            return this.snapshotRequest$;
        }

        const request$ = this.casinoGameListService.getLiveCasinoGames(0, 500).pipe(
            map((response: any) => {
                if (Array.isArray(response)) {
                    return response;
                }

                if (Array.isArray(response?.content)) {
                    return response.content;
                }

                return [];
            }),
            tap((snapshot) => {
                this.snapshotCache = snapshot;
                this.snapshotCachedAt = Date.now();
            }),
            finalize(() => {
                if (!forceRefresh) {
                    this.snapshotRequest$ = null;
                }
            }),
            shareReplay(1),
        );

        if (!forceRefresh) {
            this.snapshotRequest$ = request$;
        }

        return request$;
    }

    private ensureRealtimeFeed(pragmaticGames: any[], liveGames: any[]): void {
        if (!pragmaticGames.length && !liveGames.length) {
            return;
        }
        this.startPollingFallback();
    }

    private startPollingFallback(): void {
        if (this.pollingSubscription) {
            return;
        }

        this.pollingSubscription = interval(this.pollingIntervalMs)
            .pipe(
                startWith(0),
                switchMap(() => this.getLiveSnapshot$(true)),
                map((liveGames) => this.createRealtimeMap(liveGames)),
                catchError(() => of(new Map(this.realtimeUpdates$.value))),
            )
            .subscribe((updates) => {
                this.realtimeUpdates$.next(updates);
            });
    }

    private mergeLiveGames(snapshot: any[], realtimeMap: Map<string, any>): any[] {
        if (!realtimeMap.size) {
            return snapshot;
        }

        const merged = [...snapshot];
        const existingIndexByKey = new Map<string, number>();

        merged.forEach((liveGame, index) => {
            const keys = this.resolveLiveKeys(liveGame);
            keys.forEach((key) => existingIndexByKey.set(key, index));
        });

        realtimeMap.forEach((liveGame) => {
            const keys = this.resolveLiveKeys(liveGame);
            const existingIndex = keys.find((key) => existingIndexByKey.has(key));

            if (existingIndex) {
                merged[existingIndexByKey.get(existingIndex)!] = liveGame;
                return;
            }

            const newIndex = merged.push(liveGame) - 1;
            keys.forEach((key) => existingIndexByKey.set(key, newIndex));
        });

        return merged;
    }

    private createRealtimeMap(liveGames: any[]): Map<string, any> {
        const realtimeMap = new Map<string, any>();
        liveGames.forEach((liveGame) => {
            const tableId = liveGame?.tableId ?? liveGame?.id ?? liveGame?.gameId ?? liveGame?.gameID;
            if (tableId !== undefined && tableId !== null && tableId !== '') {
                realtimeMap.set(String(tableId), liveGame);
            }
        });
        return realtimeMap;
    }

    private decorateGames(games: any[], liveGames: any[]): any[] {
        const { liveById, liveByName } = this.buildLiveLookupMaps(liveGames);

        return [...games]
            .map((game) => this.decorateGame(game, liveById, liveByName))
            .sort((left, right) => this.sortGames(left, right));
    }

    private buildLiveLookupMaps(liveGames: any[]): {
        liveById: Map<string, any>;
        liveByName: Map<string, any>;
    } {
        const liveById = new Map<string, any>();
        const liveByName = new Map<string, any>();

        liveGames.forEach((liveGame) => {
            this.resolveLiveKeys(liveGame).forEach((key) => {
                if (key.startsWith('id:')) {
                    liveById.set(key.slice(3), liveGame);
                }

                if (key.startsWith('name:')) {
                    liveByName.set(key.slice(5), liveGame);
                }
            });
        });

        return { liveById, liveByName };
    }

    private resolveLiveKeys(liveGame: any): string[] {
        const keys: string[] = [];
        const idCandidates = [liveGame?.tableId, liveGame?.gameId, liveGame?.gameID, liveGame?.id];
        idCandidates
            .filter((value) => value !== undefined && value !== null && value !== '')
            .forEach((value) => keys.push(`id:${String(value)}`));

        const nameCandidates = [
            liveGame?.tableName,
            liveGame?.name,
            liveGame?.gameName,
            liveGame?.languageSpecificTableInfo,
        ];
        nameCandidates
            .filter((value) => typeof value === 'string' && value.trim())
            .forEach((value: string) => keys.push(`name:${this.normalizeValue(value)}`));

        return keys;
    }

    private decorateGame(game: any, liveById: Map<string, any>, liveByName: Map<string, any>): any {
        if (!this.isPragmaticGame(game)) {
            return game;
        }

        const match = this.matchLiveGame(game, liveById, liveByName);
        const status: PragmaticLiveStatus = match ? this.resolveStatus(match) : 'offline';

        return {
            ...game,
            imagePortrait: match?.tableImage || game.imagePortrait,
            imageSquare: match?.tableImage || game.imageSquare,
            pragmaticLiveStatus: status,
            pragmaticLiveTableId: match?.tableId ?? game.tableId ?? null,
            pragmaticLiveDealer: match?.dealer?.name || '',
            pragmaticLivePlayers: this.resolvePlayers(match),
            pragmaticLiveMinBet: match?.tableLimits?.minBet ?? null,
            pragmaticLiveMaxBet: match?.tableLimits?.maxBet ?? null,
            pragmaticLiveMaxPlayers: match?.tableLimits?.maxPlayers ?? null,
            pragmaticLiveTableType: match?.tableType || '',
            pragmaticLiveTableSubtype: match?.tableSubtype || '',
            pragmaticLiveLanguageInfo: match?.languageSpecificTableInfo || '',
            pragmaticLiveAvailableSeats: match?.availableSeats ?? null,
            pragmaticLiveShuffle: Boolean(match?.shuffle),
            pragmaticLiveStatistics: match?.statistics ?? null,
            pragmaticLiveLastResults: match?.last20Results ?? match?.gameResult ?? [],
            pragmaticLiveBaccaratShoeSummary: match?.baccaratShoeSummary ?? null,
            pragmaticLiveNewTable: Boolean(match?.newTable),
        };
    }

    private matchLiveGame(game: any, liveById: Map<string, any>, liveByName: Map<string, any>): any | null {
        const idCandidates = [game?.tableId, game?.externalGameId, game?.gameId, game?.id];
        for (const candidate of idCandidates) {
            if (candidate !== undefined && candidate !== null && candidate !== '') {
                const match = liveById.get(String(candidate));
                if (match) {
                    return match;
                }
            }
        }

        const nameCandidates = [game?.tableName, game?.name, game?.displayName];
        for (const candidate of nameCandidates) {
            if (typeof candidate === 'string' && candidate.trim()) {
                const match = liveByName.get(this.normalizeValue(candidate));
                if (match) {
                    return match;
                }
            }
        }

        return null;
    }

    private resolveStatus(liveGame: any): PragmaticLiveStatus {
        if (typeof liveGame?.tableOpen === 'boolean') {
            return liveGame.tableOpen ? 'live' : 'offline';
        }

        if (typeof liveGame?.isLive === 'boolean') {
            return liveGame.isLive ? 'live' : 'offline';
        }

        if (typeof liveGame?.live === 'boolean') {
            return liveGame.live ? 'live' : 'offline';
        }

        return 'unknown';
    }

    private resolvePlayers(liveGame: any): number | null {
        const playerCandidates = [
            liveGame?.totalSeatedPlayers,
            liveGame?.playerCount,
            liveGame?.numberOfPlayers,
        ];

        const resolved = playerCandidates.find(
            (value) => typeof value === 'number' && Number.isFinite(value),
        );

        return resolved ?? null;
    }

    private sortGames(left: any, right: any): number {
        const leftRank = this.statusRank(left?.pragmaticLiveStatus);
        const rightRank = this.statusRank(right?.pragmaticLiveStatus);

        if (leftRank !== rightRank) {
            return leftRank - rightRank;
        }

        if (Boolean(left?.pragmaticLiveNewTable) !== Boolean(right?.pragmaticLiveNewTable)) {
            return left?.pragmaticLiveNewTable ? -1 : 1;
        }

        return 0;
    }

    private statusRank(status?: PragmaticLiveStatus): number {
        switch (status) {
            case 'live':
                return 0;
            case 'unknown':
                return 1;
            default:
                return 2;
        }
    }

    private isPragmaticGame(game: any): boolean {
        const providerCandidates = [
            game?.subcategory,
            game?.provider,
            game?.category,
            game?.providerCategory,
            game?.provider?.providerName,
            game?.provider?.source,
        ];

        return providerCandidates.some((candidate) => this.isPragmaticProvider(candidate));
    }

    private isPragmaticProvider(value: unknown): boolean {
        if (typeof value !== 'string') {
            return false;
        }

        const normalized = this.normalizeValue(value);
        return normalized.includes('pragmaticplay') || normalized.includes('pppragmaticplay');
    }

    private normalizeValue(value: string): string {
        return value.toLowerCase().replace(/[^a-z0-9]/g, '');
    }
}
